'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { getGoogleAnalyticsClientId } from '@hubxco/hubx-smartlink';
import { Paddle } from '@paddle/paddle-js';

import { hubxAnalytic } from '@/app/providers/analytics';
import { paymentService } from '@/features/subscription/services/paymentService';
import {
  buildTransactionSignature,
  transactionPromiseStore,
} from '@/features/subscription/utils/checkout/transactionPromiseStore';
import { checkoutRevealStore } from '@/features/subscription/utils/checkout/checkoutRevealStore';
import { checkoutVisibilityStore } from '@/features/subscription/utils/checkout/checkoutVisibilityStore';
import { getCheckoutCustomerAddress } from '@/features/subscription/utils/paddle/paddlePostalCode';
import useAuth from '@/hooks/useAuth';
import PersistentCheckoutHost from '@/features/subscription/components/PersistentCheckoutHost';

export type CheckoutTransactionParams = {
  priceId: string;
  discountId?: string;
  quantity?: number;
  toolType?: string;
  paywallType?: string;
};

export type CheckoutPrewarmParams = Pick<
  CheckoutTransactionParams,
  'priceId' | 'toolType' | 'paywallType' | 'quantity'
>;

type CheckoutPrewarmContextValue = {
  ensureTransaction: (params: CheckoutTransactionParams) => Promise<string>;
  invalidateTransaction: (priceId?: string) => void;
  prefetchCheckout: (params: CheckoutPrewarmParams) => Promise<string | null>;
};

const CheckoutPrewarmContext =
  createContext<CheckoutPrewarmContextValue | null>(null);

export const CheckoutPrewarmProvider = ({
  children,
  paddleInstance,
}: {
  children: ReactNode;
  paddleInstance: Paddle | null;
}) => {
  const { user } = useAuth();
  const activePriceIdRef = useRef<string | null>(null);
  const activePriceVersionRef = useRef(0);
  const userStateKey = `${user?.uid ?? ''}:${user?.email ?? ''}:${user?.planUid ?? ''}`;
  const previousUserStateKeyRef = useRef(userStateKey);

  const activatePrice = useCallback((priceId: string) => {
    if (activePriceIdRef.current !== priceId) {
      transactionPromiseStore.deleteOtherPrices(priceId);
      activePriceIdRef.current = priceId;
      activePriceVersionRef.current += 1;
    }
  }, []);

  const createTransaction = useCallback(
    async ({
      priceId,
      discountId,
      quantity,
      toolType,
      paywallType,
    }: CheckoutTransactionParams) => {
      if (!user?.email) {
        throw new Error('User email is undefined.');
      }

      const response = await paymentService.createTransaction({
        priceId,
        trackingId: getGoogleAnalyticsClientId(),
        hubxId: hubxAnalytic.getUUID(),
        paywallType: paywallType || 'in_app',
        discountId,
        quantity,
        toolType,
      });

      const transactionId = response.data?.id;
      if (!transactionId) {
        throw new Error('Missing transaction id');
      }

      return transactionId;
    },
    [user?.email]
  );

  const createStoredTransaction = useCallback(
    (params: CheckoutTransactionParams) => {
      const promise = createTransaction(params);
      transactionPromiseStore.set(
        params.priceId,
        promise,
        buildTransactionSignature(params)
      );

      void promise.catch(() => {
        transactionPromiseStore.delete(params.priceId, promise);
      });

      return promise;
    },
    [createTransaction]
  );

  const openInHiddenHost = useCallback(
    (transactionId: string) => {
      if (!paddleInstance) {
        return;
      }

      if (checkoutRevealStore.getLoadedTransactionId() === transactionId) {
        return;
      }

      if (checkoutVisibilityStore.getVisibleTransactionId()) {
        return;
      }

      if (checkoutRevealStore.getLoadedTransactionId()) {
        paddleInstance.Checkout.close();
      }

      const customerAddress = getCheckoutCustomerAddress();
      const checkoutCustomer = user?.customerId
        ? { id: user.customerId, ...(customerAddress && { address: customerAddress }) }
        : user?.email
          ? { email: user.email, ...(customerAddress && { address: customerAddress }) }
          : undefined;

      paddleInstance.Checkout.open({
        transactionId,
        ...(checkoutCustomer ? { customer: checkoutCustomer } : {}),
        settings: {
          displayMode: 'inline',
          variant: 'one-page',
          allowLogout: false,
          showAddDiscounts: false,
          showAddTaxId: false,
          frameTarget: 'checkout-container',
          frameInitialHeight: 560,
          frameStyle:
            'width: 100%; min-width: 312px; min-height: 560px; background-color: transparent; border: none;',
        },
      });

      checkoutRevealStore.setLoadedTransactionId(transactionId);
    },
    [paddleInstance, user?.customerId, user?.email]
  );

  const prefetchCheckout = useCallback(
    async ({ priceId, toolType, paywallType, quantity }: CheckoutPrewarmParams) => {
      if (!user?.email) {
        return null;
      }

      activatePrice(priceId);

      const prefetchParams: CheckoutTransactionParams = {
        priceId,
        quantity: quantity ?? 1,
        toolType,
        paywallType,
      };
      const signature = buildTransactionSignature(prefetchParams);

      const promise =
        transactionPromiseStore.get(priceId, signature) ??
        createStoredTransaction(prefetchParams);

      try {
        const transactionId = await promise;
        if (!transactionPromiseStore.isCurrent(priceId, promise)) {
          return null;
        }

        openInHiddenHost(transactionId);
        return transactionId;
      } catch {
        return null;
      }
    },
    [activatePrice, createStoredTransaction, openInHiddenHost, user?.email]
  );

  const ensureTransaction = useCallback(
    async (params: CheckoutTransactionParams) => {
      if (!user?.email) {
        throw new Error('User email is undefined.');
      }

      activatePrice(params.priceId);

      const signature = buildTransactionSignature(params);
      const prefetchedPromise = transactionPromiseStore.get(
        params.priceId,
        signature
      );
      if (prefetchedPromise) {
        const expiresAt = transactionPromiseStore.getExpiresAt(
          params.priceId,
          prefetchedPromise
        );
        const priceVersion = activePriceVersionRef.current;

        try {
          const transactionId = await prefetchedPromise;
          if (
            expiresAt &&
            Date.now() < expiresAt &&
            activePriceIdRef.current === params.priceId &&
            activePriceVersionRef.current === priceVersion
          ) {
            transactionPromiseStore.delete(params.priceId, prefetchedPromise);
            return transactionId;
          }
        } catch {
          transactionPromiseStore.delete(params.priceId, prefetchedPromise);
        }
      }

      const transactionPromise = createStoredTransaction(params);

      try {
        return await transactionPromise;
      } finally {
        transactionPromiseStore.delete(params.priceId, transactionPromise);
      }
    },
    [activatePrice, createStoredTransaction, user?.email]
  );

  const invalidateTransaction = useCallback((priceId?: string) => {
    if (priceId) {
      transactionPromiseStore.delete(priceId);
      if (activePriceIdRef.current === priceId) {
        activePriceIdRef.current = null;
        activePriceVersionRef.current += 1;
      }
      return;
    }

    transactionPromiseStore.clear();
    activePriceIdRef.current = null;
    activePriceVersionRef.current += 1;
  }, []);

  useEffect(() => {
    if (previousUserStateKeyRef.current === userStateKey) {
      return;
    }

    transactionPromiseStore.clear();
    checkoutRevealStore.setLoadedTransactionId(null);
    activePriceIdRef.current = null;
    activePriceVersionRef.current += 1;
    previousUserStateKeyRef.current = userStateKey;
  }, [userStateKey]);

  useEffect(
    () => () => {
      transactionPromiseStore.clear();
      checkoutRevealStore.setLoadedTransactionId(null);
    },
    []
  );

  const value = useMemo(
    () => ({
      ensureTransaction,
      invalidateTransaction,
      prefetchCheckout,
    }),
    [ensureTransaction, invalidateTransaction, prefetchCheckout]
  );

  return (
    <CheckoutPrewarmContext.Provider value={value}>
      {children}
      <PersistentCheckoutHost />
    </CheckoutPrewarmContext.Provider>
  );
};

export const useCheckoutPrewarm = () => {
  const context = useContext(CheckoutPrewarmContext);

  if (!context) {
    throw new Error(
      'useCheckoutPrewarm must be used within CheckoutPrewarmProvider'
    );
  }

  return context;
};
